
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Zap, Play, Clock, Eye, AlertCircle, X, ExternalLink, Search, SlidersHorizontal } from 'lucide-react';



const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001';

// Platform icons as inline SVGs
const YouTubeIcon = () => (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
        <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
    </svg>
);

const TikTokIcon = () => (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
        <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
    </svg>
);

const ShortsIcon = () => (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
        <path d="M10 14.65v-5.3L15 12l-5 2.65zm7.77-4.33-1.2-.5L18 9.06c1.84-.96 2.53-3.23 1.56-5.06s-3.24-2.53-5.07-1.56L6 6.94c-1.29.68-2.07 2.04-2 3.49.07 1.42.93 2.67 2.22 3.25.03.01 1.2.5 1.2.5L6 14.93c-1.83.97-2.53 3.24-1.56 5.07.97 1.83 3.24 2.53 5.07 1.56l8.5-4.5c1.29-.68 2.06-2.04 1.99-3.49-.07-1.42-.94-2.68-2.23-3.25z" />
    </svg>
);

// Video Preview Modal Component
const VideoPreviewModal = ({ video, platform, onClose, onUseVideo }) => {
    // Handle escape key to close modal
    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', handleEscape);
        document.body.style.overflow = 'hidden';
        return () => {
            document.removeEventListener('keydown', handleEscape);
            document.body.style.overflow = '';
        };
    }, [onClose]);

    // Extract video ID for embedding
    const getEmbedUrl = () => {
        if (platform === 'tiktok') {
            // TikTok embed URL
            const videoId = video.id || video.url?.match(/video\/(\d+)/)?.[1];
            return `https://www.tiktok.com/embed/v2/${videoId}`;
        } else {
            // YouTube/Shorts embed URL
            const videoId = video.id || video.url?.match(/(?:v=|shorts\/|youtu\.be\/)([^&?/]+)/)?.[1];
            return `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`;
        }
    };

    const isVertical = platform === 'tiktok' || platform === 'shorts';

    return (
        <div className="video-preview-overlay" onClick={onClose}>
            <div
                className={`video-preview-modal ${isVertical ? 'vertical' : 'horizontal'}`}
                onClick={(e) => e.stopPropagation()}
            >
                <button className="video-preview-close" onClick={onClose}>
                    <X size={24} />
                </button>

                <div className="video-preview-player">
                    <iframe
                        src={getEmbedUrl()}
                        title={video.title}
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                    />
                </div>

                <div className="video-preview-info">
                    <h3 className="video-preview-title">{video.title}</h3>
                    <p className="video-preview-channel">{video.channel}</p>
                    <div className="video-preview-stats">
                        <span><Eye size={14} /> {video.viewCountFormatted} views</span>
                        <span>{video.publishedAtFormatted}</span>
                    </div>

                    <div className="video-preview-actions">
                        <button className="video-preview-use-btn" onClick={() => onUseVideo(video)}>
                            Use This Video
                        </button>
                        <a
                            href={video.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="video-preview-external-link"
                        >
                            <ExternalLink size={16} />
                            Open Original
                        </a>
                    </div>
                </div>
            </div>
        </div>
    );
};


export const TrendingPage = () => {
    const [videos, setVideos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [cacheInfo, setCacheInfo] = useState(null);
    const [platform, setPlatform] = useState('youtube'); // 'youtube', 'shorts', or 'tiktok'
    const [selectedVideo, setSelectedVideo] = useState(null); // For modal
    const navigate = useNavigate();

    // Search state
    const [searchMode, setSearchMode] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [searchFilters, setSearchFilters] = useState({
        order: 'relevance',
        duration: 'any',
        published: 'any'
    });
    const [showFilters, setShowFilters] = useState(false);

    const fetchTrending = async (selectedPlatform = platform) => {
        try {
            setLoading(true);
            setError('');
            setSearchMode(false);
            setSearchQuery('');

            let source, limit;
            if (selectedPlatform === 'tiktok') {
                source = 'tiktok';
                limit = 30;
            } else if (selectedPlatform === 'shorts') {
                source = 'shorts';
                limit = 100;
            } else {
                source = 'youtube_api';
                limit = 100;
            }
            const url = `${API_BASE}/api/trending?source=${source}&limit=${limit}`;
            const res = await fetch(url);
            const json = await res.json();

            if (json.error && json.videos?.length === 0) {
                throw new Error(json.error);
            }

            setVideos(json.videos || []);
            setCacheInfo({
                cached: json.cached,
                cacheAge: json.cacheAge,
                nextRefresh: json.nextRefresh,
                region: json.region
            });

            // Show warning if there's an error but we still have cached data
            if (json.error && json.videos?.length > 0) {
                setError(json.error);
            }
        } catch (err) {
            setError(err.message);
            setVideos([]);
        } finally {
            setLoading(false);
        }
    };

    const searchVideos = async (query = searchInput, filters = searchFilters) => {
        if (!query.trim()) return;

        try {
            setLoading(true);
            setError('');
            setSearchMode(true);
            setSearchQuery(query);
            setCacheInfo(null);

            const params = new URLSearchParams({
                q: query.trim(),
                order: filters.order,
                duration: platform === 'shorts' ? 'short' : filters.duration, // Force short duration for Shorts tab
                published: filters.published,
                limit: 50,
                shorts_only: platform === 'shorts' ? 'true' : 'false'
            });


            const res = await fetch(`${API_BASE}/api/search?${params}`);
            const json = await res.json();

            if (json.error) {
                throw new Error(json.error);
            }

            setVideos(json.videos || []);
        } catch (err) {
            setError(err.message);
            setVideos([]);
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (e) => {
        e.preventDefault();
        if (searchInput.trim()) {
            searchVideos(searchInput, searchFilters);
        }
    };

    const handleFilterChange = (filterName, value) => {
        const newFilters = { ...searchFilters, [filterName]: value };
        setSearchFilters(newFilters);
        // Re-search with new filters if already in search mode
        if (searchMode && searchQuery) {
            searchVideos(searchQuery, newFilters);
        }
    };

    const clearSearch = () => {
        setSearchInput('');
        setSearchQuery('');
        setSearchMode(false);
        setSearchFilters({ order: 'relevance', duration: 'any', published: 'any' });
        fetchTrending(platform);
    };

    useEffect(() => {
        fetchTrending(platform);
    }, [platform]);


    const handlePlatformChange = (newPlatform) => {
        if (newPlatform !== platform) {
            setPlatform(newPlatform);
            setVideos([]);
            setCacheInfo(null);
        }
    };

    const handleVideoClick = (video) => {
        setSelectedVideo(video);
    };

    const handleCloseModal = useCallback(() => {
        setSelectedVideo(null);
    }, []);

    const handleUseVideo = (video) => {
        setSelectedVideo(null);
        navigate('/downloader', { state: { url: video.url } });
    };

    const formatTime = (seconds) => {
        if (!seconds || seconds <= 0) return 'Just now';
        if (seconds < 60) return `${seconds}s`;
        const mins = Math.floor(seconds / 60);
        if (mins < 60) return `${mins}m`;
        const hours = Math.floor(mins / 60);
        const remainingMins = mins % 60;
        return remainingMins > 0 ? `${hours}h ${remainingMins}m` : `${hours}h`;
    };

    const getPlatformSubtitle = () => {
        if (platform === 'tiktok') {
            return 'Top trending videos on TikTok';
        } else if (platform === 'shorts') {
            return 'Top trending YouTube Shorts';
        }
        return 'Top 100 trending videos on YouTube';
    };

    return (
        <div className="page-container trending-page">
            <div className="trending-header">
                <div className="trending-title-section">
                    <h1 className="page-title">
                        <Zap className="trending-icon" size={28} />
                        Trending Now
                    </h1>
                    <p className="trending-subtitle">{getPlatformSubtitle()}</p>
                </div>

                <div className="trending-controls">
                    {cacheInfo && (
                        <div className="cache-timing">
                            <span className="cache-info">
                                <Clock size={14} />
                                Updated {formatTime(cacheInfo.cacheAge)} {formatTime(cacheInfo.cacheAge) === 'Just now' ? '' : 'ago'}
                            </span>
                            <span className="cache-next">
                                Next refresh in {formatTime(cacheInfo.nextRefresh)}
                            </span>
                        </div>
                    )}
                </div>
            </div>

            {/* Platform Selector Tabs */}
            <div className="platform-tabs">
                <button
                    className={`platform-tab ${platform === 'youtube' ? 'active' : ''}`}
                    onClick={() => handlePlatformChange('youtube')}
                >
                    <YouTubeIcon />
                    <span>YouTube</span>
                </button>
                <button
                    className={`platform-tab ${platform === 'shorts' ? 'active' : ''}`}
                    onClick={() => handlePlatformChange('shorts')}
                >
                    <ShortsIcon />
                    <span>Shorts</span>
                </button>
                <button
                    className={`platform-tab ${platform === 'tiktok' ? 'active' : ''}`}
                    onClick={() => handlePlatformChange('tiktok')}
                >
                    <TikTokIcon />
                    <span>TikTok</span>
                </button>
            </div>

            {/* Search Bar - YouTube only */}
            {(platform === 'youtube' || platform === 'shorts') && (
                <div className="search-section">
                    <form className="search-container" onSubmit={handleSearch}>
                        <div className="search-bar-unified">
                            <input
                                type="text"
                                className="search-input"
                                placeholder="Search"
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                            />
                            {searchInput && (
                                <button
                                    type="button"
                                    className="search-clear"
                                    onClick={() => {
                                        if (searchMode) {
                                            clearSearch();
                                        } else {
                                            setSearchInput('');
                                        }
                                    }}
                                >
                                    <X size={16} />
                                </button>
                            )}
                            <button type="submit" className="search-button" disabled={!searchInput.trim()}>
                                <Search size={20} />
                            </button>
                        </div>
                        <button
                            type="button"
                            className={`filter-toggle ${showFilters ? 'active' : ''}`}
                            onClick={() => setShowFilters(!showFilters)}
                        >
                            <SlidersHorizontal size={18} />
                        </button>
                    </form>


                    {/* Filter dropdowns */}
                    {showFilters && (
                        <div className="search-filters">
                            <div className="filter-group">
                                <label>Sort by</label>
                                <select
                                    value={searchFilters.order}
                                    onChange={(e) => handleFilterChange('order', e.target.value)}
                                >
                                    <option value="relevance">Relevance</option>
                                    <option value="viewCount">View count</option>
                                    <option value="date">Upload date</option>
                                    <option value="rating">Rating</option>
                                </select>
                            </div>
                            <div className="filter-group">
                                <label>Duration</label>
                                <select
                                    value={searchFilters.duration}
                                    onChange={(e) => handleFilterChange('duration', e.target.value)}
                                >
                                    <option value="any">Any</option>
                                    <option value="short">Short (&lt; 4 min)</option>
                                    <option value="medium">Medium (4-20 min)</option>
                                    <option value="long">Long (&gt; 20 min)</option>
                                </select>
                            </div>
                            <div className="filter-group">
                                <label>Upload date</label>
                                <select
                                    value={searchFilters.published}
                                    onChange={(e) => handleFilterChange('published', e.target.value)}
                                >
                                    <option value="any">Any time</option>
                                    <option value="hour">Last hour</option>
                                    <option value="today">Today</option>
                                    <option value="week">This week</option>
                                    <option value="month">This month</option>
                                    <option value="year">This year</option>
                                </select>
                            </div>
                        </div>
                    )}
                </div>
            )}


            {loading && (
                <div className="trending-loading">
                    <div className="loading-spinner"></div>
                    <p>{searchMode ? 'Searching...' : `Loading ${platform === 'tiktok' ? 'TikTok' : platform === 'shorts' ? 'Shorts' : 'YouTube'} trending videos...`}</p>
                </div>
            )}


            {error && !loading && videos.length === 0 && (
                <div className="trending-error">
                    <AlertCircle size={24} />
                    <p>{error}</p>
                    <button onClick={() => fetchTrending()}>Try Again</button>
                </div>
            )}

            {error && videos.length > 0 && (
                <div className="trending-warning">
                    <AlertCircle size={16} />
                    <span>Using cached data: {error}</span>
                </div>
            )}

            {!loading && !error && videos.length === 0 && (
                <div className="trending-empty">
                    <Zap size={48} />
                    <p>No trending videos available</p>
                    {platform === 'tiktok' && (
                        <p className="trending-empty-hint">
                            Make sure TIKTOK_MS_TOKEN is configured in your .env file
                        </p>
                    )}
                </div>
            )}

            <div className="trending-grid">
                {videos.map((video, index) => (
                    <div
                        key={video.id}
                        className={`trending-card ${platform === 'tiktok' || platform === 'shorts' ? 'tiktok-card' : ''}`}
                        onClick={() => handleVideoClick(video)}
                    >
                        <div className="trending-card-rank">#{index + 1}</div>

                        <div className="trending-card-thumbnail">
                            <img
                                src={video.thumbnail}
                                alt={video.title}
                                loading="lazy"
                            />
                            <div className="trending-card-overlay">
                                <Play size={40} />
                            </div>
                            <div className="trending-card-duration">
                                {video.durationFormatted}
                            </div>
                            {video.isShort && platform === 'youtube' && (
                                <div className="trending-card-short-badge">
                                    Short
                                </div>
                            )}
                            {platform === 'shorts' && (
                                <div className="trending-card-platform-badge shorts">
                                    <ShortsIcon />
                                </div>
                            )}
                            {platform === 'tiktok' && (
                                <div className="trending-card-platform-badge tiktok">
                                    <TikTokIcon />
                                </div>
                            )}
                        </div>

                        <div className="trending-card-info">
                            <h3 className="trending-card-title">{video.title}</h3>
                            <p className="trending-card-channel">{video.channel}</p>
                            <div className="trending-card-stats">
                                <span className="trending-card-views">
                                    <Eye size={14} />
                                    {video.viewCountFormatted} views
                                </span>
                                <span className="trending-card-date">
                                    {video.publishedAtFormatted}
                                </span>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Video Preview Modal */}
            {selectedVideo && (
                <VideoPreviewModal
                    video={selectedVideo}
                    platform={platform}
                    onClose={handleCloseModal}
                    onUseVideo={handleUseVideo}
                />
            )}
        </div>
    );
};
